import type { ComputedRef, MaybeRefOrGetter, WritableComputedRef } from 'vue';
import type { BlockDefinition, BlockSerialized, BlockSerializedChild, BlockSlotDefinition } from '#layers/block-engine/types/block';
import { computed, inject, toValue } from 'vue';
import { callOrReturn } from '#layers/base/public';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useBlockConfigApi } from '#layers/block-engine/utils/block/api/config';
import { useBlockChild } from '#layers/block-engine/utils/block/api/context';
import { horizontalLayoutKey } from '#layers/block-engine/utils/block/const';
/* Helpers for blocks */
import { useBlockConfig } from './config';
import { useBlockProgrammaticChildAdapter } from './programmatic-child';

export function useHorizontalLayout() {
	const injected = inject(horizontalLayoutKey, false);
	return computed(() => toValue(injected));
}

/**
 * @internal
 */
export function useBlockSlots(block: MaybeRefOrGetter<BlockDefinition | BlockSerialized | undefined>, child: MaybeRefOrGetter<BlockSerializedChild | undefined>, blockProps: MaybeRefOrGetter<Record<string, any>>): ComputedRef<BlockSlotDefinition[]> {
	const config = useBlockConfig(block);
	const blockConfigApiBase = useBlockConfigApi(child);

	return computed(() => {
		if (config.value?.slots) {
			return callOrReturn(config.value.slots, { props: toValue(blockProps), ...blockConfigApiBase }).map(
				(slotOrName) => typeof slotOrName === 'string' ? { name: slotOrName } : slotOrName,
			) ?? [];
		}

		return toValue(block)?.slots ?? [];
	});
}

/**
 * Allows to get and set the value of a prop of the current block child.
 *
 * Only works for a component that is rendered in the Block Editor UI.
 *
 * @param key Prop name
 * @returns A writable computed ref with the prop value
 */
export function useEditorBlockProp<V>(key: string): WritableComputedRef<V> {
	const getChild = useBlockChild();
	const adapter = useBlockProgrammaticChildAdapter(getChild);

	const { updateChildProp } = useBlockEditorContext();

	return computed({
		get() {
			if (adapter) {
				return adapter.getProps()?.[key] as V;
			}

			return getChild()?.props?.[key] as V;
		},
		set(value: V) {
			if (adapter) {
				adapter.setProp(key, value);
				return;
			}

			const child = getChild();

			if (child) {
				updateChildProp(child.id, key, value);
			}
		},
	});
}
